import { Flatten } from '@doveit/bricks';
import {
  isEqual as lodashIsEqual, merge, isUndefined,
  isEqualWith,
  isObject,
  keys,
  union,
} from 'lodash';

export const isKeyOf = <T extends object>(obj: T, key: string | number | symbol): key is keyof T => key in obj;

export function flattenObject<TObj extends Record<string, any>>(obj: TObj, prefix: string = ''): Flatten<TObj> {
  return Object.keys(obj).reduce((acc, key) => {
    if (isKeyOf(obj, key)) {
      const value = obj[key];

      const path = `${prefix ? `${prefix}.` : ''}${key}` as string & keyof Flatten<TObj>;

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        return {
          ...acc,
          ...flattenObject(value, path),
        };
      }

      acc[path] = value;
    }

    return acc;
  }, {} as Flatten<TObj>);
}

export function isEmpty(object: any): boolean {
  return Object.values(object).every((el) => el === undefined || el === null);
}

export const deepMerge = <T extends object, U extends object>(a: T, b: U): T & U => merge({}, a, b);

export interface IsEqualOptions {
  /**
   * If true, treats undefined properties as non-existent.
   */
  loose?: boolean;
  /**
   * If true, string comparisons are case-insensitive.
   */
  caseInsensitive?: boolean;
}

/**
 * Performs a deep equality check between two values.
 * @param a - The value to compare.
 * @param b - The other value to compare.
 * @param options - Configuration options for the equality check.
 * @returns True if the values are equivalent, else false.
 */
export const isEqual = (
  a: any,
  b: any,
  options: IsEqualOptions = {},
): boolean => {
  if (!options.loose && !options.caseInsensitive) {
    return lodashIsEqual(a, b);
  }

  return isEqualWith(a, b, (val1, val2) => {
    if (options.caseInsensitive && typeof val1 === 'string' && typeof val2 === 'string') {
      return val1.toLowerCase() === val2.toLowerCase();
    }

    if (options.loose && isObject(val1) && isObject(val2)) {
      const allKeys = union(keys(val1), keys(val2));

      return allKeys.every((key) => {
        const v1 = (val1 as any)[key];
        const v2 = (val2 as any)[key];

        if (isUndefined(v1) && isUndefined(v2)) {
          return true;
        }

        return isEqual(v1, v2, options);
      });
    }

    return undefined;
  });
};
